import { useState, useEffect, useCallback } from 'react';
import { Plus, X, Trash2, Check, CheckCircle2, ArrowUpCircle, ArrowDownCircle, MinusCircle, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

interface Todo {
  id: string;
  title: string;
  isCompleted: boolean;
  priority: 'low' | 'medium' | 'high';
  createdAt: string;
}

const priorityConfig = {
  high: { color: 'text-coral', bg: 'bg-coral-soft', icon: ArrowUpCircle, label: 'Hoch' },
  medium: { color: 'text-sun-deep', bg: 'bg-sun-soft', icon: MinusCircle, label: 'Mittel' },
  low: { color: 'text-sage-deep', bg: 'bg-sage-soft', icon: ArrowDownCircle, label: 'Niedrig' },
};

export default function TodosView() {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [form, setForm] = useState({ title: '', priority: 'medium' as Todo['priority'] });
  const [filter, setFilter] = useState<'all' | 'active' | 'done'>('all');

  const loadTodos = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await api.todos.list();
      setTodos(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTodos();
  }, [loadTodos]);

  const openCreate = () => {
    setEditingTodo(null);
    setForm({ title: '', priority: 'medium' });
    setShowAddDialog(true);
  };

  const openEdit = (todo: Todo) => {
    setEditingTodo(todo);
    setForm({ title: todo.title, priority: todo.priority });
    setShowAddDialog(true);
  };

  const closeDialog = () => {
    setShowAddDialog(false);
    setEditingTodo(null);
    setForm({ title: '', priority: 'medium' });
  };

  const handleSave = async () => {
    if (!form.title.trim() || isSaving) return;
    try {
      setIsSaving(true);
      setError(null);
      if (editingTodo) {
        await api.todos.update(editingTodo.id, { title: form.title, priority: form.priority });
        setTodos((prev) =>
          prev.map((t) => (t.id === editingTodo.id ? { ...t, title: form.title, priority: form.priority } : t))
        );
      } else {
        const created = await api.todos.create({ title: form.title, priority: form.priority });
        setTodos((prev) => [...prev, created]);
      }
      closeDialog();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleTodo = async (todo: Todo) => {
    const next = !todo.isCompleted;
    try {
      await api.todos.update(todo.id, { isCompleted: next });
      setTodos((prev) => prev.map((t) => (t.id === todo.id ? { ...t, isCompleted: next } : t)));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const deleteTodo = async (id: string) => {
    try {
      await api.todos.delete(id);
      setTodos((prev) => prev.filter((t) => t.id !== id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const filteredTodos = todos.filter((t) => {
    if (filter === 'active') return !t.isCompleted;
    if (filter === 'done') return t.isCompleted;
    return true;
  });

  const doneCount = todos.filter((t) => t.isCompleted).length;
  const totalCount = todos.length;
  const progress = totalCount > 0 ? (doneCount / totalCount) * 100 : 0;

  // ==================== ADD/EDIT DIALOG ====================
  if (showAddDialog) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="sl-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="sl-h2">{editingTodo ? 'Todo bearbeiten' : 'Neues Todo'}</h2>
            <button onClick={closeDialog} className="p-2 rounded-lg hover:bg-paper-deep transition-colors">
              <X className="w-5 h-5 text-ink-mute" />
            </button>
          </div>

          {error && <p className="text-coral text-sm mb-4">{error}</p>}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">Was muss erledigt werden? *</label>
              <input
                type="text"
                placeholder="z.B. Arbeitsblätter korrigieren"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="sl-input"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">Priorität</label>
              <div className="flex gap-2">
                {(Object.keys(priorityConfig) as Array<keyof typeof priorityConfig>).map((p) => {
                  const config = priorityConfig[p];
                  const Icon = config.icon;
                  return (
                    <button
                      key={p}
                      onClick={() => setForm({ ...form, priority: p })}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        form.priority === p
                          ? `${config.bg} ${config.color}`
                          : 'bg-paper-deep text-ink-soft hover:bg-paper'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {config.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <button onClick={closeDialog} className="sl-button sl-button-outline">
                Abbrechen
              </button>
              <button
                onClick={handleSave}
                disabled={!form.title.trim() || isSaving}
                className="sl-button sl-button-primary disabled:opacity-50 gap-2"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingTodo ? 'Speichern' : 'Erstellen'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== LOADING ====================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-cobalt animate-spin" />
      </div>
    );
  }

  // ==================== MAIN VIEW ====================
  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="sl-h2">Todo-Liste</h2>
          <p className="text-sm text-ink-mute mt-1">{doneCount} von {totalCount} erledigt</p>
        </div>
        <button onClick={openCreate} className="sl-button sl-button-primary gap-2">
          <Plus className="w-5 h-5" />
          Neues Todo
        </button>
      </div>

      {error && (
        <div className="sl-card p-3 mb-4 border-coral bg-coral-soft text-coral text-sm">{error}</div>
      )}

      {totalCount > 0 && (
        <div className="mb-6">
          <div className="h-2 bg-paper-deep rounded-full overflow-hidden">
            <div
              className="h-full bg-cobalt rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-ink-mute mt-1">{Math.round(progress)}% erledigt</p>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {[
          { id: 'all', label: 'Alle' },
          { id: 'active', label: 'Offen' },
          { id: 'done', label: 'Erledigt' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id as typeof filter)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              filter === f.id
                ? 'bg-cobalt text-white'
                : 'bg-paper-deep text-ink-soft hover:bg-paper'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {todos.length === 0 ? (
        <div className="sl-card p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-ink-faint mx-auto mb-4" />
          <h3 className="sl-h3 mb-2 text-ink-soft">Noch keine Todos</h3>
          <p className="text-ink-mute mb-6">Erstelle deine ersten Aufgaben und behalte den Überblick.</p>
          <button onClick={openCreate} className="sl-button sl-button-primary gap-2">
            <Plus className="w-5 h-5" />
            Erstes Todo erstellen
          </button>
        </div>
      ) : filteredTodos.length === 0 ? (
        <div className="sl-card p-8 text-center">
          <p className="text-ink-mute">Keine Todos in dieser Ansicht.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTodos.map((todo) => {
            const config = priorityConfig[todo.priority] ?? priorityConfig.medium;
            const PriorityIcon = config.icon;
            return (
              <div
                key={todo.id}
                className={`sl-card p-4 flex items-center gap-3 group transition-all ${
                  todo.isCompleted ? 'opacity-60' : ''
                }`}
              >
                <button
                  onClick={() => toggleTodo(todo)}
                  className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    todo.isCompleted
                      ? 'bg-sage border-sage text-white'
                      : 'border-ink-faint hover:border-cobalt'
                  }`}
                >
                  {todo.isCompleted && <Check className="w-4 h-4" />}
                </button>

                <div className="flex-1 min-w-0">
                  <p className={`font-medium truncate ${todo.isCompleted ? 'line-through text-ink-mute' : 'text-ink'}`}>
                    {todo.title}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-xs ${config.color} flex items-center gap-1`}>
                      <PriorityIcon className="w-3 h-3" />
                      {config.label}
                    </span>
                    <span className="text-xs text-ink-faint">
                      {new Date(todo.createdAt).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                </div>

                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openEdit(todo)}
                    className="p-2 rounded-lg hover:bg-paper-deep text-ink-mute hover:text-ink"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteTodo(todo.id)}
                    className="p-2 rounded-lg hover:bg-coral-soft text-ink-mute hover:text-coral"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
